// NOMBASE — Runtime config (Doppler-backed).
//
// CRA bakes process.env.REACT_APP_* into the bundle at BUILD time. That
// means rotating a public key in Doppler requires a full frontend
// rebuild — not what we want when Doppler is the source of truth.
//
// This module fetches the same keys from the backend at boot. The backend
// hydrates its env from Doppler on startup (services/doppler.py), then
// serves the public-safe subset at /api/config/public. The frontend
// awaits this fetch BEFORE initialising Sentry / PostHog, then mounts
// React. The "build-time fallback" pattern still works — if a
// REACT_APP_* var happens to be baked in, it overrides the runtime value
// (lets ops force-pin a key from the deploy UI).
//
// Failure mode: if /api/config/public is unreachable (offline, 503),
// the bootstrap proceeds with empty values — Sentry / PostHog each
// silently no-op. The app shell still renders so the user can see a
// reasonable error state instead of a blank screen.
//
// Note on Clerk: `clerk_publishable_key` is still fetched here for
// runtime validation/diagnostics, but as of May 2026 no Clerk SDK
// runs in the browser and no `<ClerkProvider/>` is mounted. Identity
// is established server-side via `/api/auth/dev-bootstrap` minting an
// HS256 `nombase_session` cookie. The key is kept reachable so a
// future reconnect of a frontend Clerk SDK has a single, env-driven
// source of truth ready to go.

let _cached = null;

const EMPTY = Object.freeze({
  posthog_key: '',
  posthog_host: 'https://us.i.posthog.com',
  // Session Replay + autocapture are HEAVY on a large SPA. Default off
  // so production users never pay that CPU/bandwidth tax unless an
  // operator explicitly opts in via Doppler. Sample rate caps replay
  // overhead even when enabled.
  posthog_session_recording: false,
  posthog_session_recording_sample_rate: 0.1,
  posthog_autocapture: false,
  sentry_dsn: '',
});

// Build-time fallback: REACT_APP_* baked in by CRA. When set, takes
// precedence over the runtime fetch (allows hot-pinning from deploy UI).
const BUILD_TIME = Object.freeze({
  posthog_key:           process.env.REACT_APP_POSTHOG_KEY || '',
  posthog_host:          process.env.REACT_APP_POSTHOG_HOST || '',
  sentry_dsn:            process.env.REACT_APP_SENTRY_DSN || '',
});

function _backendBase() {
  // Same logic as services/api.js — REACT_APP_BACKEND_URL is required and
  // is the ONE env var the deploy UI must always set.
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return base.replace(/\/$/, '');
}

/**
 * Fetch + cache the public config. Idempotent — repeat calls return
 * the same Promise. Resolves with a fully-populated config object even
 * on network failure (falls back to build-time + EMPTY).
 */
export async function loadRuntimeConfig() {
  if (_cached) return _cached;
  _cached = (async () => {
    let runtime = EMPTY;
    try {
      const resp = await fetch(`${_backendBase()}/api/config/public`, {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
      });
      if (resp.ok) {
        const body = await resp.json();
        runtime = { ...EMPTY, ...body };
      }
    } catch (_) {
      // Network failure — fall through to BUILD_TIME / EMPTY.
    }
    // Build-time wins when set; runtime fills the gaps.
    return Object.freeze({
      posthog_key:           BUILD_TIME.posthog_key           || runtime.posthog_key,
      posthog_host:          BUILD_TIME.posthog_host          || runtime.posthog_host || EMPTY.posthog_host,
      // Booleans/numbers come strictly from runtime — no build-time
      // override needed; flipping a flag in Doppler shouldn't require
      // a rebuild.
      posthog_session_recording:
        runtime.posthog_session_recording === true,
      posthog_session_recording_sample_rate:
        typeof runtime.posthog_session_recording_sample_rate === 'number'
          ? runtime.posthog_session_recording_sample_rate
          : EMPTY.posthog_session_recording_sample_rate,
      posthog_autocapture:
        runtime.posthog_autocapture === true,
      sentry_dsn:            BUILD_TIME.sentry_dsn            || runtime.sentry_dsn,
    });
  })();
  return _cached;
}

/**
 * Synchronous accessor — only valid AFTER loadRuntimeConfig() has resolved.
 * Returns the cached config or a safe default. Used by lib/clerk.jsx where
 * the JSX render is sync.
 */
let _resolved = null;
export function getRuntimeConfig() {
  return _resolved || { ...EMPTY };
}

/** Internal — called by index.js after loadRuntimeConfig() resolves. */
export function _setResolved(cfg) { _resolved = cfg; }
